import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { DebtProductRowData } from '../../modules/DebtProducts/debtProductTypes';

/**
 * Base selectors for normalized debt products state
 */
export const selectDebtProductsState = (state: RootState) => state.debtProducts;

export const selectProductsById = (state: RootState) => state.debtProducts.productsById;
export const selectLendersById = (state: RootState) => state.debtProducts.lendersById;
export const selectProductOrder = (state: RootState) => state.debtProducts.productOrder;
export const selectHeaders = (state: RootState) => state.debtProducts.headers;
export const selectVisibilityStates = (state: RootState) => state.debtProducts.visibilityStates;

/**
 * Selector to get a single product by ID
 */
export const selectProductById = (productId: string) => createSelector([selectProductsById], (productsById) => productsById[productId]);

/**
 * Selector to get a single lender by composite key (lenderId_productId)
 */
export const selectLenderByKey = (lenderId: string, productId: string) =>
    createSelector([selectLendersById], (lendersById) => lendersById[`${lenderId}_${productId}`]);

/**
 * Selector to get all lenders for a specific product
 */
export const selectLendersByProductId = (productId: string) =>
    createSelector([selectProductsById, selectLendersById], (productsById, lendersById) => {
        const product = productsById[productId];
        if (!product || !product.lenderIds) return [];
        return product.lenderIds.map((lenderKey) => lendersById[lenderKey]).filter(Boolean);
    });

/**
 * Main selector: Denormalizes the state back to the original row format for rendering
 * This is memoized and only recomputes when the underlying data changes
 */
export const selectDebtProductsRows = createSelector(
    [selectProductsById, selectLendersById, selectProductOrder],
    (productsById, lendersById, productOrder): DebtProductRowData[] => {
        return productOrder
            .map((productId) => {
                const product = productsById[productId];
                if (!product) return null;

                // Denormalize lenders back to nested array
                const lenders: DebtProductRowData[] = (product.lenderIds || [])
                    .map((lenderKey) => lendersById[lenderKey])
                    .filter(Boolean) as DebtProductRowData[];

                return {
                    ...product,
                    lenders,
                } as DebtProductRowData;
            })
            .filter(Boolean) as DebtProductRowData[];
    },
);

/**
 * Selector to get the full denormalized state (for backward compatibility)
 * Returns the same shape as the original non-normalized state
 */
export const selectDebtProductsData = createSelector(
    [selectDebtProductsRows, selectHeaders, selectVisibilityStates],
    (rows, headers, visibilityStates) => ({
        rows,
        headers,
        visibilityStates,
    }),
);

/**
 * Selector to get only expanded products (for optimized rendering)
 */
export const selectExpandedProductIds = createSelector([selectProductsById, selectProductOrder], (productsById, productOrder) =>
    productOrder.filter((productId) => productsById[productId]?.expand),
);

/**
 * Selector to check if a product is expanded
 */
export const selectIsProductExpanded = (productId: string) =>
    createSelector([selectProductsById], (productsById) => productsById[productId]?.expand ?? false);

/**
 * Selector to count total lenders across all products
 */
export const selectTotalLendersCount = createSelector([selectLendersById], (lendersById) => Object.keys(lendersById).length);

/**
 * Selector to get products with their lender counts
 */
export const selectProductsWithLenderCounts = createSelector([selectProductsById, selectProductOrder], (productsById, productOrder) =>
    productOrder.map((productId) => ({
        productId,
        productName: productsById[productId]?.productName,
        lenderCount: productsById[productId]?.lenderIds?.length ?? 0,
    })),
);

/**
 * Utility function to generate composite key for lenders
 */
export const getLenderKey = (lenderId: string, productId: string): string => `${lenderId}_${productId}`;

/**
 * Utility function to parse composite key back to lenderId and productId
 */
export const parseLenderKey = (key: string): { lenderId: string; productId: string } => {
    const lastUnderscoreIndex = key.lastIndexOf('_');
    return {
        lenderId: key.substring(0, lastUnderscoreIndex),
        productId: key.substring(lastUnderscoreIndex + 1),
    };
};
